import React, { useEffect, useState } from "react";
import Icon from "../../assets/icons";
import Tabs from "../../components/tab";
import useRequest from "../../components/hooks/use-request";
import Requests from "./support/requests";
import Pagination from "../../components/pagination/pagination";
import Agents from "./agents/agents";
import SupportLog from "./audit/support-log";
import CircleLoader from "react-spinners/CircleLoader";
import { TailSpin } from "react-loader-spinner";

interface UserData {
  fullname: string;
}

const CustomerSupport = () => {
  const [activeTab, setActiveTab] = useState<
    "requests" | "agents" | "audit logs"
  >("requests");
  const params = new URLSearchParams(new URL(window.location.href).search);
  const [data, setData] = useState<UserData[]>([]);
  const [logData, setLogData] = useState<UserData[]>([]);
  const [agentData, setAgentData] = useState<UserData[]>([]);
  const [statistics, setStatistics] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const userToken = localStorage.getItem("token");
  const [selectedStatus, setSelectedStatus] = useState<string>("");
  const { makeRequest, loading: ticketsLoading } = useRequest(
    "/customer-support/tickets",
    "GET"
  );
  const { makeRequest: getAgent, loading: agentsLoading } = useRequest(
    "/customer-support/agents",
    "GET"
  );
  const { makeRequest: getSupportLogs, loading: logsLoading } = useRequest(
    "/audit-logs/customer-support",
    "GET"
  );
  const { makeRequest: getStats } = useRequest(
    "/customer-support/statistics",
    "GET"
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const itemsPerPage = 10;

  const updateUrlParams = (params: { [key: string]: string | number }) => {
    const url = new URL(window.location.href);
    Object.keys(params).forEach((key) => {
      url.searchParams.set(key, params[key].toString());
    });
    window.history.pushState({}, "", url.toString());
  };

  useEffect(() => {
    const storedSearchQuery = params.get("search") || "";
    const storedStatus = params.get("status") || "";
    const storedUserType = params.get("type") || activeTab;

    setSearchQuery(storedSearchQuery);
    setSelectedStatus(storedStatus);
    setActiveTab(storedUserType as "requests" | "agents");
    fetchData();
  }, [currentPage]);

  useEffect(() => {
    updateUrlParams({
      page: currentPage,
      search: searchQuery,
      status: selectedStatus,
      type: activeTab,
    });
    fetchData();
  }, [activeTab, searchQuery, selectedStatus, currentPage]);

  const fetchData = async () => {
    const page = currentPage;
    const limit = itemsPerPage;
    const params: {
      limit: number;
      page: number;
      status?: string;
      name?: string;
      search?: string;
    } = {
      limit,
      page,
    };

    if (selectedStatus) {
      params.status = selectedStatus;
    }

    if (searchQuery) {
      params.name = searchQuery;
    }

    if (searchQuery) {
      params.search = searchQuery;
    }

    if (activeTab === "requests") {
      const [response] = await makeRequest(undefined, params);
      setData(response.data?.data?.tickets || []);
      setTotalPages(Math.ceil(response.data?.data?.totalPages));
    } else if (activeTab === "agents") {
      const [response] = await getAgent(undefined, params);
      setAgentData(response.data?.agents || []);
      setTotalPages(Math.ceil(response.data?.totalPages));
    } else if (activeTab === "audit logs") {
      const [response] = await getSupportLogs(undefined, { page, limit });
      setLogData(response.data || []);
      setTotalPages(Math.ceil(response.data?.totalPages));
    }
  };

  const handleSearchChange = (event: any) => {
    setSearchQuery(event.target.value);
  };

  const handleStatusChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedStatus(event.target.value);
  };

  function handlePageChange(page: number) {
    setCurrentPage(page);
  }

  useEffect(() => {
    const fetchData = async () => {
      const [response] = await getStats();
      setStatistics(response.data);
    };

    fetchData();
  }, []);

  return (
    <>
      <div className='flex justify-between w-[95%]'>
        <section>
          <h2 className='text-[24px] font-bold'>
            Customer support{" "}
            <p className='text-[14px] font-normal'>
              See all customer support requests
            </p>
          </h2>
        </section>

        <section className='flex gap-4'>
          <div className='border-2 rounded-md solid pl-5 bg-transparent h-[45px] flex gap-3'>
            <Icon name='searchIcon' className='mt-3 rounded' />
            <input
              className='outline-none border-none w-[80%] bg-transparent'
              id='input-placeholder'
              placeholder='Search'
              value={searchQuery}
              onChange={handleSearchChange}
            />
          </div>
          <Icon name='downloadIcon' />
          <Icon name='notificationIcon' />
        </section>
      </div>

      <Tabs
        activeTab={activeTab}
        tabs={["requests", "agents", "audit logs"]}
        setActiveTab={setActiveTab}
      />
      {ticketsLoading || agentsLoading || logsLoading ? (
        <div className='flex justify-center items-center h-64'>
          <TailSpin color='skyblue' />
        </div>
      ) : (
        <>
          {activeTab === "requests" && (
            <Requests
              data={data}
              statistics={statistics}
              selectedStatus={selectedStatus}
              handleStatusChange={handleStatusChange}
            />
          )}

          {activeTab === "agents" && <Agents data={agentData} />}

          {activeTab === "audit logs" && <SupportLog data={logData} />}

          {totalPages > 1 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          )}
        </>
      )}
    </>
  );
};

export default CustomerSupport;
